/**
 * Sensar Player bundle entry point.
 *
 * This is the single file the shell .wgt loads off Cloudflare Pages.
 * It exposes one function — `start(options)` — that the shell invokes
 * after sha256-verifying the downloaded bytes.
 *
 * The shell hands us the boardId (when known) and metadata about both
 * versions so logs are correctly attributed. We hand back a `stop()`
 * function the shell can use to tear us down (e.g. for an immediate
 * rollback).
 *
 * Important: `start` MUST be safe to call multiple times — the shell
 * may re-invoke it after a rollback or a forced re-init. We expose
 * a single `mountedRoot` we clean up between starts.
 */

import { createRoot, type Root } from 'react-dom/client';
import App from './App';
import { uploadPlayerLogs } from './core/api-client';
import { logger } from './core/logger';
import {
  captureException,
  captureLoggedError,
  initSentry,
  setSentryContext,
} from './core/sentry';
import { migrateLegacyStorageKeys } from './core/storage-keys';
import { startWebUpdater } from './core/web-updater';
import { probeTimeSync } from './core/time-sync';
import { markBootStart, markFirstRender } from './core/perf';
import { initializeCache } from './playback/media-cache';
import { markBootStarted, reportError } from './health/watchdog';

export interface StartOptions {
  /** Where to mount React. The shell creates and passes the element. */
  mountTarget: HTMLElement;
  /** Bundle semver string for log attribution. */
  bundleVersion: string;
  /** Shell semver — read from tizen.application.appInfo.version. */
  shellVersion?: string;
  /** Already-known boardId, if the shell paired before invoking us. */
  boardId?: string;
}

let mountedRoot: Root | null = null;
let cleanupErrorListeners: (() => void) | null = null;

export interface PlayerHandle {
  /** Tear down React + listeners. Used for rollback. */
  stop(): Promise<void>;
}

export async function start(opts: StartOptions): Promise<PlayerHandle> {
  // Arm the boot watchdog FIRST so even an exception during the boot
  // path counts as a boot failure and increments the safe-mode counter.
  // App.tsx calls markBootSucceeded() once the first user-facing screen
  // mounts. The perf instrument captures wall-clock for the same span.
  markBootStarted();
  markBootStart();

  // First-time wiring — only do once across re-invocations.
  if (!mountedRoot) {
    // Run the senso_* → sensar_* localStorage migration BEFORE anything
    // else reads from storage. Idempotent — no-op once the device has
    // already been booted on the new key prefix.
    migrateLegacyStorageKeys();

    // Initialise Sentry FIRST so it captures any throws during the
    // remaining boot path. Safe no-op if init has already run.
    initSentry();
    logger.setErrorSink((message, context) => {
      captureLoggedError(message, context);
    });

    logger.setUploader(async (batch, meta) => {
      await uploadPlayerLogs(
        batch.map((rec) => ({
          level: rec.level,
          message: rec.message,
          context: rec.context,
          timestamp: rec.timestamp,
          bundleVersion: meta.bundleVersion,
          shellVersion: meta.shellVersion,
        })),
      );
    });

    const onError = (event: ErrorEvent) => {
      reportError('window.onerror', event.error ?? event.message);
      // Capture the Error directly so Sentry can group by stack trace.
      // captureLoggedError (called by logger.error) only sees the string.
      captureException(event.error ?? new Error(event.message), {
        source: event.filename,
        line: event.lineno,
        col: event.colno,
      });
      logger.error('uncaught error', {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        col: event.colno,
        stack: (event.error as Error | undefined)?.stack,
      });
    };
    const onReject = (event: PromiseRejectionEvent) => {
      reportError('unhandledrejection', event.reason);
      captureException(event.reason, { kind: 'unhandled-rejection' });
      logger.error('unhandled promise rejection', {
        reason: String(event.reason),
        stack: (event.reason as Error | undefined)?.stack,
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onReject);
    cleanupErrorListeners = () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onReject);
    };

    // Pre-warm the media cache index so PlayerScreen mounts with
    // entries already known. Non-blocking — startup proceeds while it
    // runs.
    void initializeCache();
  }

  logger.setMeta({
    bundleVersion: opts.bundleVersion,
    shellVersion: opts.shellVersion,
  });
  setSentryContext({
    bundleVersion: opts.bundleVersion,
    shellVersion: opts.shellVersion,
    boardId: opts.boardId,
  });
  if (opts.boardId) logger.start(opts.boardId);

  // URL Launcher / browser modes need an explicit auto-update mechanism
  // because no shell is polling /manifest.json on their behalf. No-op
  // when running inside the .wgt shell.
  startWebUpdater({
    loadedVersion: opts.bundleVersion,
    boardId: opts.boardId,
  });

  // Verify clock drift one-shot at boot. Don't await — we don't want
  // to delay PlayerScreen on a slow API. A 5-min cron-like re-probe
  // happens inside sync-manager's normal cycle (via the same
  // /health connectivity check).
  void probeTimeSync();

  logger.info('Sensar Player bundle started', {
    bundleVersion: opts.bundleVersion,
    shellVersion: opts.shellVersion,
    ts: Date.now(),
  });

  // Replace any prior root before mounting fresh.
  if (mountedRoot) {
    try {
      mountedRoot.unmount();
    } catch {
      /* ignore */
    }
  }
  mountedRoot = createRoot(opts.mountTarget);
  mountedRoot.render(<App />);
  // React commits asynchronously; queueMicrotask runs after the first
  // commit on the same tick.
  queueMicrotask(() => markFirstRender());

  return {
    stop: async () => {
      logger.info('Sensar Player bundle stopping');
      if (mountedRoot) {
        try {
          mountedRoot.unmount();
        } catch {
          /* ignore */
        }
        mountedRoot = null;
      }
      cleanupErrorListeners?.();
      cleanupErrorListeners = null;
      logger.stop();
    },
  };
}

// Expose a default export too so a UMD-style consumer can do
// `bundle.default.start(...)` — useful if the shell loads us via
// `import()` rather than a global assignment.
export default { start };

// Optional ambient: register on window for the shell to find by name.
// The shell knows to look at `window.__SENSAR_PLAYER__.start`.
declare global {
  interface Window {
    __SENSAR_PLAYER__?: { start: typeof start };
  }
}
window.__SENSAR_PLAYER__ = { start };
